import { entries, isArray } from 'ag-charts-core';
import type {
    AgChartTheme,
    AgChartThemeOptions,
    AgChartThemeOverrides,
    AgChartThemePalette,
    AgChartThemeParams,
    AgCommonThemeableChartOptions,
    AgPaletteColors,
    AgPresetOverrides,
    AgThemeOverrides,
    CssColor,
    WithThemeParams,
} from 'ag-charts-types';

import { type PaletteType, paletteType } from '../../module/coreModulesTypes';
import { enterpriseModule } from '../../module/enterpriseModule';
import { Color } from '../../util/color';
import { deepClone, jsonWalk } from '../../util/json';
import { deepFreeze, mergeDefaults } from '../../util/object';
import { axisRegistry } from '../factory/axisRegistry';
import { type ChartType, chartDefaults, chartTypes } from '../factory/chartTypes';
import { legendRegistry } from '../factory/legendRegistry';
import { seriesRegistry } from '../factory/seriesRegistry';
import { CARTESIAN_AXIS_TYPE, CARTESIAN_POSITION, FONT_SIZE, FONT_SIZE_RATIO, POLAR_AXIS_TYPE } from './constants';
import { DEFAULT_FILLS, DEFAULT_STROKES, type DefaultColors } from './defaultColors';
import {
    DEFAULT_ANNOTATION_HANDLE_FILL,
    DEFAULT_ANNOTATION_STATISTICS_COLOR,
    DEFAULT_ANNOTATION_STATISTICS_DIVIDER_STROKE,
    DEFAULT_ANNOTATION_STATISTICS_DOWN_FILL,
    DEFAULT_ANNOTATION_STATISTICS_DOWN_STROKE,
    DEFAULT_ANNOTATION_STATISTICS_FILL,
    DEFAULT_ANNOTATION_STATISTICS_STROKE,
    DEFAULT_CAPTION_ALIGNMENT,
    DEFAULT_CAPTION_LAYOUT_STYLE,
    DEFAULT_FIBONACCI_STROKES,
    DEFAULT_FINANCIAL_CHARTS_ANNOTATION_BACKGROUND_FILL,
    DEFAULT_FINANCIAL_CHARTS_ANNOTATION_COLOR,
    DEFAULT_GRIDLINE_ENABLED,
    DEFAULT_POLAR_SERIES_STROKE,
    DEFAULT_SEPARATION_LINES_COLOUR,
    DEFAULT_SHADOW_COLOUR,
    DEFAULT_SPARKLINE_CROSSHAIR_STROKE,
    DEFAULT_TEXTBOX_COLOR,
    DEFAULT_TEXTBOX_FILL,
    DEFAULT_TEXTBOX_STROKE,
    DEFAULT_TEXT_ANNOTATION_COLOR,
    DEFAULT_TOOLBAR_POSITION,
    IS_COMMUNITY,
    IS_DARK_THEME,
    IS_ENTERPRISE,
    PALETTE_ALT_DOWN_FILL,
    PALETTE_ALT_DOWN_STROKE,
    PALETTE_ALT_NEUTRAL_FILL,
    PALETTE_ALT_NEUTRAL_STROKE,
    PALETTE_ALT_UP_FILL,
    PALETTE_ALT_UP_STROKE,
    PALETTE_DOWN_FILL,
    PALETTE_DOWN_STROKE,
    PALETTE_NEUTRAL_FILL,
    PALETTE_NEUTRAL_STROKE,
    PALETTE_UP_FILL,
    PALETTE_UP_STROKE,
} from './symbols';
import { getSequentialColors } from './util';

// If this changes, update plugins/ag-charts-generate-chart-thumbnail/src/executors/generate/generator/constants.ts
const DEFAULT_BACKGROUND_FILL = 'white';

type ChartTypeConfig = {
    seriesTypes: string[];
    commonOptions: (keyof AgCommonThemeableChartOptions)[];
};

const CHART_TYPE_CONFIG: { [k in ChartType]: ChartTypeConfig } = {
    get cartesian(): ChartTypeConfig {
        return { seriesTypes: chartTypes.cartesianTypes, commonOptions: ['zoom', 'navigator'] };
    },
    get polar(): ChartTypeConfig {
        return { seriesTypes: chartTypes.polarTypes, commonOptions: [] };
    },
    get hierarchy(): ChartTypeConfig {
        return { seriesTypes: chartTypes.hierarchyTypes, commonOptions: [] };
    },
    get topology(): ChartTypeConfig {
        return { seriesTypes: chartTypes.topologyTypes, commonOptions: [] };
    },
    get 'flow-proportion'(): ChartTypeConfig {
        return { seriesTypes: chartTypes.flowProportionTypes, commonOptions: [] };
    },
    get standalone(): ChartTypeConfig {
        return { seriesTypes: chartTypes.standaloneTypes, commonOptions: [] };
    },
    get gauge(): ChartTypeConfig {
        return { seriesTypes: chartTypes.gaugeTypes, commonOptions: [] };
    },
};

type OverridesKey = keyof AgThemeOverrides;

const PRESET_OVERRIDES_TYPES: Record<keyof AgPresetOverrides, true> = {
    'radial-gauge': true,
    'linear-gauge': true,
};

function isPresetOverridesType(type: OverridesKey): type is keyof AgPresetOverrides {
    return PRESET_OVERRIDES_TYPES[type as keyof AgPresetOverrides] === true;
}

const CHART_TYPE_SPECIFIC_COMMON_OPTIONS = Object.values(CHART_TYPE_CONFIG).reduce<
    (keyof AgCommonThemeableChartOptions)[]
>((r, { commonOptions }) => r.concat(commonOptions), []);

export class ChartTheme {
    readonly palette: Required<AgChartThemePalette> & {
        sequentialColors: CssColor[][]; // TODO: AG-14186 make public
        altUp: AgPaletteColors;
        altDown: AgPaletteColors;
        altNeutral: AgPaletteColors;
    };
    readonly paletteType: PaletteType;

    readonly config: any;
    readonly presets: AgPresetOverrides;

    public static getDefaultColors(): DefaultColors {
        return {
            fills: DEFAULT_FILLS,
            fillsFallback: Object.values(DEFAULT_FILLS),
            strokes: DEFAULT_STROKES,
            sequentialColors: getSequentialColors(DEFAULT_FILLS),
            divergingColors: [DEFAULT_FILLS.ORANGE, DEFAULT_FILLS.YELLOW, DEFAULT_FILLS.GREEN],
            hierarchyColors: ['#fff', '#e0e5ea', '#c1ccd5', '#a3b4c1', '#859cad'],
            secondSequentialColors: Color.interpolate(
                [
                    Color.fromHexString(DEFAULT_FILLS.BLUE),
                    Color.fromHexString('#cbdef5'), // TODO: Color.lighten(DEFAULT_FILLS.BLUE, ?)
                ],
                8
            ).map((color) => color.toString()),
            secondDivergingColors: [DEFAULT_FILLS.GREEN, DEFAULT_FILLS.YELLOW, DEFAULT_FILLS.RED],
            secondHierarchyColors: ['#fff', '#c5cbd1', '#a4b1bd', '#8498a9', '#648096'],
            up: { fill: DEFAULT_FILLS.GREEN, stroke: DEFAULT_STROKES.GREEN },
            down: { fill: DEFAULT_FILLS.RED, stroke: DEFAULT_STROKES.RED },
            neutral: { fill: DEFAULT_FILLS.GRAY, stroke: DEFAULT_STROKES.GRAY },
            altUp: { fill: DEFAULT_FILLS.BLUE, stroke: DEFAULT_STROKES.BLUE },
            altDown: { fill: DEFAULT_FILLS.ORANGE, stroke: DEFAULT_STROKES.ORANGE },
            altNeutral: { fill: DEFAULT_FILLS.GRAY, stroke: DEFAULT_STROKES.GRAY },
        };
    }

    public static getDefaultPublicParameters(): Required<WithThemeParams<AgChartThemeParams>> {
        return {
            accentColor: '#2196f3',
            axisColor: { $foregroundBackgroundMix: [0.675] },
            backgroundColor: DEFAULT_BACKGROUND_FILL,
            borderColor: { $foregroundBackgroundMix: [0.818] },
            foregroundColor: '#464646',
            fontFamily: 'Verdana, sans-serif',
            fontSize: FONT_SIZE.SMALL,
            fontWeight: 400,
            gridLineColor: { $foregroundBackgroundAccentMix: [0.93, 0.085] },
            padding: 20,
            subtleTextColor: { $mix: [{ $ref: 'textColor' }, { $ref: 'backgroundColor' }, 0.38] },
            textColor: { $ref: 'foregroundColor' },

            chromeBackgroundColor: { $foregroundBackgroundMix: [0.975] },
            chromeFontFamily: { $ref: 'fontFamily' },
            chromeFontSize: { $ref: 'fontSize' },
            chromeFontWeight: { $ref: 'fontWeight' },
            chromeTextColor: '#181d1f',
            chromeSubtleTextColor: { $mix: [{ $ref: 'chromeTextColor' }, { $ref: 'backgroundColor' }, 0.38] },

            inputBackgroundColor: { $ref: 'backgroundColor' },
            inputTextColor: { $ref: 'textColor' },

            crosshairLabelBackgroundColor: { $ref: 'foregroundColor' },
            crosshairLabelTextColor: { $ref: 'backgroundColor' },
        };
    }

    private static getAxisDefaults(overrideDefaults: object, withTitle = true) {
        return mergeDefaults(
            overrideDefaults,
            withTitle && {
                title: {
                    enabled: false,
                    text: 'Axis Title',
                    spacing: 25,
                    fontWeight: { $ref: 'fontWeight' },
                    fontSize: { $rem: [FONT_SIZE_RATIO.MEDIUM] },
                    fontFamily: { $ref: 'fontFamily' },
                    color: { $ref: 'textColor' },
                },
            },
            {
                label: {
                    fontSize: { $ref: 'fontSize' },
                    fontFamily: { $ref: 'fontFamily' },
                    fontWeight: { $ref: 'fontWeight' },
                    spacing: 11,
                    color: { $ref: 'textColor' },
                    avoidCollisions: true,
                },
                line: {
                    enabled: true,
                    width: 1,
                    stroke: { $ref: 'axisColor' },
                },
                tick: {
                    enabled: false,
                    width: 1,
                    stroke: { $ref: 'axisColor' },
                },
                gridLine: {
                    enabled: true,
                    style: [{ stroke: { $ref: 'gridLineColor' }, lineDash: [] }],
                },
                crossLines: {
                    enabled: true,
                    fill: { $ref: 'foregroundColor' },
                    stroke: { $ref: 'foregroundColor' },
                    fillOpacity: 0.1,
                    strokeWidth: 1,
                    label: {
                        fontSize: { $ref: 'fontSize' },
                        fontFamily: { $ref: 'fontFamily' },
                        fontWeight: { $ref: 'fontWeight' },
                        padding: 5,
                        color: { $ref: 'textColor' },
                    },
                },
            }
        );
    }

    protected getChartDefaults() {
        return {
            minHeight: 300,
            minWidth: 300,
            background: { visible: true, fill: { $ref: 'backgroundColor' } },
            padding: {
                top: { $ref: 'padding' },
                right: { $ref: 'padding' },
                bottom: { $ref: 'padding' },
                left: { $ref: 'padding' },
            },
            seriesArea: {
                padding: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                },
            },
            keyboard: { enabled: true },
            title: {
                enabled: false,
                text: 'Title',
                spacing: { $if: [{ $path: '../subtitle/enabled' }, 10, 20] },
                fontWeight: { $ref: 'fontWeight' },
                fontSize: { $rem: [FONT_SIZE_RATIO.LARGEST] },
                fontFamily: { $ref: 'fontFamily' },
                color: { $ref: 'textColor' },
                wrapping: 'hyphenate',
                layoutStyle: DEFAULT_CAPTION_LAYOUT_STYLE,
                textAlign: DEFAULT_CAPTION_ALIGNMENT,
            },
            subtitle: {
                enabled: false,
                text: 'Subtitle',
                spacing: 20,
                fontWeight: { $ref: 'fontWeight' },
                fontSize: { $rem: [FONT_SIZE_RATIO.MEDIUM] },
                fontFamily: { $ref: 'fontFamily' },
                color: { $ref: 'subtleTextColor' },
                wrapping: 'hyphenate',
                layoutStyle: DEFAULT_CAPTION_LAYOUT_STYLE,
                textAlign: DEFAULT_CAPTION_ALIGNMENT,
            },
            footnote: {
                enabled: false,
                text: 'Footnote',
                spacing: 20,
                fontSize: { $rem: [FONT_SIZE_RATIO.MEDIUM] },
                fontFamily: { $ref: 'fontFamily' },
                fontWeight: { $ref: 'fontWeight' },
                color: { $ref: 'subtleTextColor' },
                wrapping: 'hyphenate',
                layoutStyle: DEFAULT_CAPTION_LAYOUT_STYLE,
                textAlign: DEFAULT_CAPTION_ALIGNMENT,
            },
            legend: {
                position: CARTESIAN_POSITION.BOTTOM,
                orientation: {
                    $if: [
                        {
                            $or: [
                                { $eq: [{ $path: './position' }, CARTESIAN_POSITION.LEFT] },
                                { $eq: [{ $path: './position' }, CARTESIAN_POSITION.RIGHT] },
                            ],
                        },
                        'vertical',
                        'horizontal',
                    ],
                },
                spacing: 30,
                listeners: {},
                toggleSeries: true,
                item: {
                    paddingX: 16,
                    paddingY: 8,
                    marker: { size: 15, padding: 8 },
                    showSeriesStroke: true,
                    label: {
                        color: { $ref: 'textColor' },
                        fontSize: { $ref: 'fontSize' },
                        fontFamily: { $ref: 'fontFamily' },
                        fontWeight: { $ref: 'fontWeight' },
                    },
                },
                reverseOrder: false,
                pagination: {
                    marker: { size: 12 },
                    activeStyle: { fill: { $ref: 'foregroundColor' } },
                    inactiveStyle: { fill: { $ref: 'subtleTextColor' } },
                    highlightStyle: { fill: { $ref: 'foregroundColor' } },
                    label: { color: { $ref: 'textColor' } },
                },
            },
            tooltip: {
                enabled: true,
                darkTheme: IS_DARK_THEME,
                delay: 0,
                pagination: false,
            },
            overlays: { darkTheme: IS_DARK_THEME },
            listeners: {},
        };
    }

    private static readonly axisDefault = {
        [CARTESIAN_AXIS_TYPE.NUMBER]: ChartTheme.getAxisDefaults({
            keys: [],
            line: { enabled: false },
            crosshair: { enabled: true },
        }),
        [CARTESIAN_AXIS_TYPE.LOG]: ChartTheme.getAxisDefaults({
            keys: [],
            base: 10,
            line: { enabled: false },
            crosshair: { enabled: true },
        }),
        [CARTESIAN_AXIS_TYPE.CATEGORY]: ChartTheme.getAxisDefaults({
            keys: [],
            groupPaddingInner: 0.1,
            label: { autoRotate: true },
            gridLine: { enabled: DEFAULT_GRIDLINE_ENABLED },
            crosshair: { enabled: false },
        }),
        [CARTESIAN_AXIS_TYPE.GROUPED_CATEGORY]: ChartTheme.getAxisDefaults({
            keys: [],
            tick: { enabled: true, stroke: DEFAULT_SEPARATION_LINES_COLOUR },
            label: { spacing: 10, rotation: 270 },
            paddingInner: 0.4,
            groupPaddingInner: 0.2,
            crosshair: { enabled: false },
        }),
        [CARTESIAN_AXIS_TYPE.TIME]: ChartTheme.getAxisDefaults({
            keys: [],
            gridLine: { enabled: DEFAULT_GRIDLINE_ENABLED },
            crosshair: { enabled: true },
        }),
        [CARTESIAN_AXIS_TYPE.ORDINAL_TIME]: ChartTheme.getAxisDefaults({
            keys: [],
            groupPaddingInner: 0,
            label: { autoRotate: false },
            gridLine: { enabled: DEFAULT_GRIDLINE_ENABLED },
            crosshair: { enabled: true },
        }),
        [POLAR_AXIS_TYPE.ANGLE_CATEGORY]: ChartTheme.getAxisDefaults(
            {
                label: { spacing: 5 },
                gridLine: { enabled: DEFAULT_GRIDLINE_ENABLED },
                shape: {
                    $path: [
                        './shape',
                        undefined,
                        { $find: [{ $not: [{ $isOperation: './shape' }] }, { $path: '..' }] },
                    ],
                },
            },
            false
        ),
        [POLAR_AXIS_TYPE.ANGLE_NUMBER]: ChartTheme.getAxisDefaults(
            {
                label: { spacing: 5 },
                gridLine: { enabled: DEFAULT_GRIDLINE_ENABLED },
            },
            false
        ),
        [POLAR_AXIS_TYPE.RADIUS_CATEGORY]: ChartTheme.getAxisDefaults({
            positionAngle: 0,
            line: { enabled: false },
        }),
        [POLAR_AXIS_TYPE.RADIUS_NUMBER]: ChartTheme.getAxisDefaults({
            positionAngle: 0,
            line: { enabled: false },
            shape: {
                $path: ['./shape', undefined, { $find: [{ $not: [{ $isOperation: './shape' }] }, { $path: '..' }] }],
            },
        }),
    };

    constructor(options: AgChartTheme = {}) {
        const { overrides, palette } = deepClone(options) as AgChartThemeOptions;
        const defaults = this.createChartConfigPerChartType(this.getDefaults());
        const presets: Record<string, any> = {};

        if (overrides) {
            this.mergeOverrides(defaults, presets, overrides);
        }

        const { fills, strokes, sequentialColors, ...otherColors } = this.getDefaultColors();
        this.palette = deepFreeze(
            mergeDefaults(palette, {
                fills: Object.values(fills),
                strokes: Object.values(strokes),
                sequentialColors: Object.values(sequentialColors),
                ...otherColors,
            })
        );
        this.paletteType = paletteType(palette);

        this.config = deepFreeze(this.templateTheme(defaults));
        this.presets = deepFreeze(presets);
    }

    private mergeOverrides(defaults: AgChartThemeOverrides, presets: AgPresetOverrides, overrides: AgThemeOverrides) {
        for (const { seriesTypes, commonOptions } of Object.values(CHART_TYPE_CONFIG)) {
            const cleanedCommon = { ...overrides.common };
            for (const commonKey of CHART_TYPE_SPECIFIC_COMMON_OPTIONS) {
                if (!commonOptions.includes(commonKey)) {
                    delete cleanedCommon[commonKey];
                }
            }
            if (!cleanedCommon) continue;
            for (const s of seriesTypes) {
                const seriesType = s as keyof AgThemeOverrides;

                if (!isPresetOverridesType(seriesType)) {
                    defaults[seriesType] = mergeDefaults(cleanedCommon, defaults[seriesType]);
                }
            }
        }

        chartTypes.seriesTypes.forEach((s) => {
            const seriesType = s as keyof AgThemeOverrides;
            const seriesOverrides = overrides[seriesType];

            if (isPresetOverridesType(seriesType)) {
                presets[seriesType] = seriesOverrides as any;
            } else {
                defaults[seriesType] = mergeDefaults(seriesOverrides, defaults[seriesType]);
            }
        });
    }

    private createChartConfigPerChartType(config: AgChartThemeOverrides) {
        for (const [nextType, { seriesTypes }] of entries(CHART_TYPE_CONFIG)) {
            const typeDefaults = chartDefaults.get(nextType);
            for (const seriesType of seriesTypes) {
                config[seriesType as keyof AgChartThemeOverrides] ??= deepClone(typeDefaults);
            }
        }
        return config;
    }

    private getDefaults(): AgChartThemeOverrides {
        const getOverridesByType = (chartType: ChartType, seriesTypes: string[]) => {
            const result: Record<string, { series?: object; axes?: object }> = {};
            const chartTypeDefaults = {
                axes: {},
                ...legendRegistry.getThemeTemplates(),
                ...this.getChartDefaults(),
                ...chartDefaults.get(chartType),
            };
            for (const seriesType of seriesTypes) {
                result[seriesType] = mergeDefaults(
                    seriesRegistry.getThemeTemplate(seriesType),
                    result[seriesType] ?? deepClone(chartTypeDefaults)
                );

                const { axes } = result[seriesType] as { axes: Record<string, object> };

                for (const axisType of axisRegistry.keys()) {
                    axes[axisType] = mergeDefaults(
                        axes[axisType],
                        axisRegistry.getThemeTemplate(axisType),
                        (ChartTheme.axisDefault as any)[axisType]
                    );
                }
            }

            return result;
        };

        return mergeDefaults(
            getOverridesByType('cartesian', chartTypes.cartesianTypes),
            getOverridesByType('polar', chartTypes.polarTypes),
            getOverridesByType('hierarchy', chartTypes.hierarchyTypes),
            getOverridesByType('topology', chartTypes.topologyTypes),
            getOverridesByType('flow-proportion', chartTypes.flowProportionTypes),
            getOverridesByType('standalone', chartTypes.standaloneTypes),
            getOverridesByType('gauge', chartTypes.gaugeTypes)
        );
    }

    private static applyTemplateTheme(this: void, node: any, _other: any, params?: Map<any, any>) {
        if (isArray(node)) {
            for (let i = 0; i < node.length; i++) {
                const symbol = node[i];
                if (typeof symbol === 'symbol' && params?.has(symbol)) {
                    node[i] = params.get(symbol);
                }
            }
        } else {
            for (const name of Object.keys(node)) {
                const value = node[name];
                if (typeof value === 'symbol' && params?.has(value)) {
                    node[name] = params.get(value);
                }
            }
        }
    }

    templateTheme<T>(themeTemplate: T, clone = true): T {
        const themeInstance = clone ? deepClone(themeTemplate) : themeTemplate;
        const params = this.getTemplateParameters();

        jsonWalk(themeInstance, ChartTheme.applyTemplateTheme, undefined, undefined, params);

        return themeInstance;
    }

    protected getDefaultColors(): DefaultColors {
        return ChartTheme.getDefaultColors();
    }

    getPublicParameters(): Required<WithThemeParams<AgChartThemeParams>> {
        return ChartTheme.getDefaultPublicParameters();
    }

    // Private parameters that are not exposed in the themes API.
    getTemplateParameters() {
        const { isEnterprise } = enterpriseModule;

        const params = new Map();
        params.set(IS_DARK_THEME, false);
        params.set(IS_ENTERPRISE, isEnterprise);
        params.set(IS_COMMUNITY, !isEnterprise);
        params.set(DEFAULT_SEPARATION_LINES_COLOUR, '#d9d9d9');
        params.set(DEFAULT_SHADOW_COLOUR, '#00000080');
        params.set(DEFAULT_SPARKLINE_CROSSHAIR_STROKE, '#aaa');
        params.set(DEFAULT_CAPTION_LAYOUT_STYLE, 'block');
        params.set(DEFAULT_CAPTION_ALIGNMENT, 'center');
        params.set(DEFAULT_FIBONACCI_STROKES, [
            '#797b86',
            '#e24c4a',
            '#f49d2d',
            '#65ab58',
            '#409682',
            '#4db9d2',
            '#5090dc',
            '#3068f9',
            '#e24c4a',
            '#913aac',
            '#d93e64',
        ]);
        params.set(DEFAULT_POLAR_SERIES_STROKE, DEFAULT_BACKGROUND_FILL);

        params.set(DEFAULT_FINANCIAL_CHARTS_ANNOTATION_COLOR, DEFAULT_FILLS.BLUE);
        params.set(DEFAULT_FINANCIAL_CHARTS_ANNOTATION_BACKGROUND_FILL, DEFAULT_FILLS.BLUE);
        params.set(DEFAULT_TEXT_ANNOTATION_COLOR, DEFAULT_FILLS.BLUE);
        params.set(DEFAULT_ANNOTATION_HANDLE_FILL, DEFAULT_BACKGROUND_FILL);
        params.set(DEFAULT_ANNOTATION_STATISTICS_FILL, '#fafafa');
        params.set(DEFAULT_ANNOTATION_STATISTICS_STROKE, '#ddd');
        params.set(DEFAULT_ANNOTATION_STATISTICS_COLOR, '#000');
        params.set(DEFAULT_ANNOTATION_STATISTICS_DIVIDER_STROKE, '#181d1f');
        params.set(DEFAULT_ANNOTATION_STATISTICS_DOWN_FILL, '#e35c5c');
        params.set(DEFAULT_ANNOTATION_STATISTICS_DOWN_STROKE, '#e35c5c');

        params.set(DEFAULT_TEXTBOX_FILL, '#fafafa');
        params.set(DEFAULT_TEXTBOX_STROKE, '#ddd');
        params.set(DEFAULT_TEXTBOX_COLOR, '#000');

        params.set(DEFAULT_TOOLBAR_POSITION, 'top');
        params.set(DEFAULT_GRIDLINE_ENABLED, false);

        const defaultColors = this.getDefaultColors();
        params.set(PALETTE_UP_STROKE, this.palette.up?.stroke ?? defaultColors.up.stroke);
        params.set(PALETTE_UP_FILL, this.palette.up?.fill ?? defaultColors.up.fill);
        params.set(PALETTE_DOWN_STROKE, this.palette.down?.stroke ?? defaultColors.down.stroke);
        params.set(PALETTE_DOWN_FILL, this.palette.down?.fill ?? defaultColors.down.fill);
        params.set(PALETTE_NEUTRAL_STROKE, this.palette.neutral?.stroke ?? defaultColors.neutral.stroke);
        params.set(PALETTE_NEUTRAL_FILL, this.palette.neutral?.fill ?? defaultColors.neutral.fill);
        params.set(PALETTE_ALT_UP_STROKE, this.palette.altUp?.stroke ?? defaultColors.up.stroke);
        params.set(PALETTE_ALT_UP_FILL, this.palette.altUp?.fill ?? defaultColors.up.fill);
        params.set(PALETTE_ALT_DOWN_STROKE, this.palette.altDown?.stroke ?? defaultColors.down.stroke);
        params.set(PALETTE_ALT_DOWN_FILL, this.palette.altDown?.fill ?? defaultColors.down.fill);
        params.set(PALETTE_ALT_NEUTRAL_FILL, this.palette.altNeutral?.fill ?? defaultColors.altNeutral.fill);
        params.set(PALETTE_ALT_NEUTRAL_STROKE, this.palette.altNeutral?.stroke ?? defaultColors.altNeutral.stroke);

        return params;
    }
}
